import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import API from '../../api';

import { useRefreshToken } from '../../common/hooks/useRefreshToken';
import Loader from '../../components/Loader';
import notify from '../../components/Notification/helper';
import ViewPage from '../../layouts/ViewPage';
import LabelAndDisplay from '../../components/LabelAndDisplay';
import ImageContainer from '../../components/shared/ImageContainer';

import Wysiwyg from '../../components/shared/Wysiwyg';

import { Tab, Tabs } from '@mui/material';
import { getTranslation } from '../../utils/translate';
import PropTypes from 'prop-types';
import RecipeGrid from '../../components/RecipeGrid';
import BeefCutsGrid from '../../components/BeefCutsGrid';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function CookingMethodView() {
  useRefreshToken();

  const params = useParams();

  const language = {
    code: 'en-US',
  };

  const retailer = useSelector(state => state.retailer);

  const cookingMethodId = params.cookingMethodId;

  const [title, setTitle] = useState('');
  const [description] = useState('');

  const [fields, setFields] = useState([]);
  const [sidebarFields, setSidebarFields] = useState([]);

  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(0);

  const recipesPayload = {
    url: `recipe?meta=*`,
    fields: [
      "*",
      "layout_translations.*",
      "imagegallery.directus_files_id",
    ],
    filter: {
      _and: [
        {
          '_or': [
            {
              retailers: {
                retailer_id: {
                  '_eq': retailer.id
                }
              }
            },
            {
              retailer: {
                id: {
                  '_eq': retailer.id
                }
              }
            }
          ]
        },
        {
          cooking_method: {
            cooking_method_id: {
              '_eq': cookingMethodId
            }
          },
        }
      ],
    },
    sort: 'date_created',
    limit: -1,
  };

  const beefCutsPayload = {
    url: `beef_product?meta=*`,
    fields: `id,date_created,parent_group,owner,svg_icon,image,retailers.retailer_id,translations.*`,
    deep: {
      beef_product_overrides: {
        _filter: {
          retailer: {
            _eq: retailer.id
          }
        }
      },
    },
    filter: {
      _and: [
        {
          '_or': [
            {
              retailers: {
                retailer_id: {
                  '_eq': retailer.id
                }
              }
            },
          ]
        },
        {
          cooking_methods: {
            cooking_method: {
              '_eq': cookingMethodId
            }
          },
        }
      ],
    },
    sort: '-date_created',
    limit: -1,
  };

  useEffect(() => {
    if (!cookingMethodId) return;

    makeGetQrCodeRequest();
  }, [cookingMethodId]);

  const makeGetQrCodeRequest = async () => {
    try {
      setLoading(true);

      const itemResponse = await API.getFrom('cooking_method', cookingMethodId);

      const translatedItem = getTranslation(itemResponse, { key: 'languages_code', code: 'en-US' });

      const image = itemResponse?.image ?
        `${process.env.REACT_APP_API_URL}/assets/${itemResponse.image}?key=tile-background-image` : null;

      setTitle(translatedItem.Method);

      setFields([
        {
          key: 'Name',
          value: translatedItem.Method,
        },
        {
          key: 'Summary',
          children: (
            <Typography>
              <Wysiwyg>{translatedItem.Description}</Wysiwyg>
            </Typography>
          )
        },
      ]);

      setSidebarFields([
        {
          key: 'Image',
          children: (
            <ImageContainer
              url={image}
              alt={translatedItem.Method}
              sx={{
                height: '20rem',
              }}
            />
          )
        },
      ])
    } catch (error) {
      console.log('error: ', error);
      notify('error', 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <ViewPage
        title={title}
        description={description}
        addToLibrary={{
          junctionTable: 'retailer_cooking_method',
          filter: {
            _and: [
              { cooking_method_id: { _eq: cookingMethodId } },
              { retailer_id: { _eq: retailer.id } }
            ]
          },
          addPayload: {
            cooking_method_id: cookingMethodId,
            retailer_id: retailer.id
          },
          removePayload: {
            query: {
              filter: {
                retailer_id: {
                  _eq: retailer.id,
                },
                cooking_method_id: {
                  _eq: cookingMethodId,
                }
              }
            }
          },
        }}
        breadcrumbsLinks={[
          {
            url: '/recipes-menu',
            content: 'Content Library',
          },
          {
            url: `/cooking-methods`,
            content: 'Cooking Methods',
          }
        ]}
      >
        <Box sx={{ flexGrow: 1, my: 3 }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              width: '100%',
              display: {
                xs: 'none',
                lg: 'block',
              }
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
            >
              <Tab label="Overview" {...a11yProps(0)} />
              <Tab label="Recipes" {...a11yProps(1)} />
              <Tab label="Beef Cuts" {...a11yProps(2)} />
            </Tabs>
          </Box>

          <CustomTabPanel value={value} index={0}>
            <Typography
              className='form-section-title'
              variant="h4"
            >
              Overview
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={7} lg={8}>
                <Box sx={{ flexGrow: 1, my: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {
                        fields.map(field => (
                          <LabelAndDisplay
                            key={field.key}
                            label={field.key}
                            value={field.value}
                          >
                            {field.children}
                          </LabelAndDisplay>
                        ))
                      }
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={5} lg={4}>
                <Box sx={{ flexGrow: 1, my: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {
                        sidebarFields.map(field => (
                          <LabelAndDisplay
                            direction='column'
                            key={field.key}
                            label={field.key}
                            value={field.value}
                          >
                            {field.children}
                          </LabelAndDisplay>
                        ))
                      }
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={1}>
            <Typography
              className='form-section-title'
              variant="h4"
            >
              Recipes
            </Typography>
            
            <Box sx={{ flexGrow: 1, my: 3 }}>
              <RecipeGrid payload={recipesPayload} language={language} />
            </Box>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={2}>
            <Typography
              className='form-section-title'
              variant="h4"
            >
              Beef Cuts
            </Typography>
            <Box sx={{ flexGrow: 1, my: 3 }}>
              <BeefCutsGrid payload={beefCutsPayload} language={language} />
            </Box>
          </CustomTabPanel>
        </Box>
      </ViewPage>
    </>
  )
}

export default CookingMethodView